import { SelectOption } from '@property.com.au/ui-kit';
import {
  SEARCH_PROPERTY_FEATURES_DEFAULT_ITEM,
  SEARCH_PROPERTY_TYPE_DEFAULT_ITEM,
} from '../helpers/defaults';

import { SelectOptionWithBadge } from '../types';
import { getPropertyConfigFiltersFromUrl } from '../helpers/queryStringHelpers/getPropertyConfigFromUrl';
import {
  getMultipleSelectOptionsByString,
  getSelectOptionByString,
} from '../helpers/getSelectOptionByString';
import { getExcludeDepositTakenOnPropertyStatusChange } from '../helpers/getExcludeDepositTakenOnPropertyStatusChange';
import { getHasImagesOnPropertyStatusChange } from '../helpers/getHasImagesOnPropertyStatusChange';
import { getMinLandSizeOrDefault } from '../helpers/getMinLandSizeOrDefault';
import { getSelectedOptionsOrDefault } from '../helpers/getSelectedOptionsOrDefault';
import { isPropertyStatusForSale } from '../helpers/isPropertyStatusForSale';

export interface PropertyConfig {
  propertyTypes: SelectOption[];
  propertyStatus: SelectOption | null;
  minBathrooms: SelectOption | null;
  minBedrooms: SelectOption | null;
  minLandSize: string;
  indoorFeatures: SelectOption[];
  outdoorFeatures: SelectOption[];
  ecoFriendlyFeatures: SelectOption[];
  minCarSpaces: SelectOption | null;
  keywords: string;
  excludeUnderOffer?: boolean;
  excludeDepositTaken: boolean;
  hasImages: boolean;
}

export type SearchPage_FilterOptions = {
  propertyStatus: SelectOptionWithBadge[];
  propertyTypes: SelectOption[];
  minBedrooms: SelectOption[];
  minBathrooms: SelectOption[];
  indoorFeatures: SelectOption[];
  outdoorFeatures: SelectOption[];
  ecoFriendlyFeatures: SelectOption[];
  minCarSpaces: SelectOption[];
};

export const createInitialState = (
  searchQuery: string,
  {
    propertyTypes: propertyTypesOptions,
    propertyStatus: propertyStatusOptions,
    minBathrooms: minBathroomsOptions,
    minBedrooms: minBedroomsOptions,
    indoorFeatures: indoorFeaturesOptions,
    outdoorFeatures: outdoorFeaturesOptions,
    ecoFriendlyFeatures: ecoFriendlyFeaturesOptions,
    minCarSpaces: minCarSpacesOptions,
  }: SearchPage_FilterOptions,
): PropertyConfig => {
  const {
    propertyStatus,
    propertyTypes,
    minBathrooms,
    minBedrooms,
    indoorFeatures,
    outdoorFeatures,
    ecoFriendlyFeatures,
    minCarSpaces,
    minLandSize,
    keywords,
    excludeUnderOffer,
    excludeDepositTaken,
    hasImages,
  } = getPropertyConfigFiltersFromUrl(searchQuery);

  return {
    propertyTypes: getMultipleSelectOptionsByString({
      value: propertyTypes,
      options: propertyTypesOptions,
    }),
    propertyStatus: getSelectOptionByString({
      value: propertyStatus,
      options: propertyStatusOptions,
    }),
    minBathrooms: getSelectOptionByString({
      value: minBathrooms,
      options: minBathroomsOptions,
    }),
    minBedrooms: getSelectOptionByString({
      value: minBedrooms,
      options: minBedroomsOptions,
    }),
    minLandSize,
    indoorFeatures: getMultipleSelectOptionsByString({
      value: indoorFeatures,
      options: indoorFeaturesOptions,
    }),
    outdoorFeatures: getMultipleSelectOptionsByString({
      value: outdoorFeatures,
      options: outdoorFeaturesOptions,
    }),
    ecoFriendlyFeatures: getMultipleSelectOptionsByString({
      value: ecoFriendlyFeatures,
      options: ecoFriendlyFeaturesOptions,
    }),
    minCarSpaces: getSelectOptionByString({
      value: minCarSpaces,
      options: minCarSpacesOptions,
    }),
    keywords: keywords?.split(',').join(', ') ?? '',
    excludeUnderOffer: excludeUnderOffer === 'true',
    excludeDepositTaken: excludeDepositTaken !== 'false',
    hasImages: hasImages !== 'false',
  };
};

export type PropertyConfigReducerActions =
  | { type: 'propertyTypes'; value: SelectOption[] }
  | { type: 'propertyStatus'; value: SelectOption }
  | { type: 'minBathrooms'; value: SelectOption }
  | { type: 'minBedrooms'; value: SelectOption }
  | { type: 'minLandSize'; value: string }
  | { type: 'indoorFeatures'; value: SelectOption[] }
  | { type: 'outdoorFeatures'; value: SelectOption[] }
  | { type: 'ecoFriendlyFeatures'; value: SelectOption[] }
  | { type: 'minCarSpaces'; value: SelectOption }
  | { type: 'keywords'; value: string }
  | { type: 'excludeUnderOffer'; value: boolean }
  | { type: 'excludeDepositTaken'; value: boolean }
  | { type: 'hasImages'; value: boolean };

export const propertyConfigReducer = (
  state: PropertyConfig,
  { type, value }: PropertyConfigReducerActions,
): PropertyConfig => {
  switch (type) {
    case 'propertyTypes':
      return {
        ...state,
        propertyTypes: getSelectedOptionsOrDefault({
          defaultValue: SEARCH_PROPERTY_TYPE_DEFAULT_ITEM,
          currentSelection: value,
          previousSelection: state.propertyTypes,
        }),
      };

    case 'propertyStatus':
      // eslint-disable-next-line no-case-declarations
      const newState = {
        ...state,
        propertyStatus: value,
        excludeDepositTaken: getExcludeDepositTakenOnPropertyStatusChange(state, value),
        hasImages: getHasImagesOnPropertyStatusChange(state, value),
      };
      if (!isPropertyStatusForSale(value.value)) delete newState.excludeUnderOffer;
      return newState;

    case 'minBathrooms':
      return { ...state, minBathrooms: value };

    case 'minBedrooms':
      return { ...state, minBedrooms: value };

    case 'minLandSize':
      return { ...state, minLandSize: getMinLandSizeOrDefault(value) };

    case 'indoorFeatures':
      return {
        ...state,
        indoorFeatures: getSelectedOptionsOrDefault({
          defaultValue: SEARCH_PROPERTY_FEATURES_DEFAULT_ITEM,
          currentSelection: value,
          previousSelection: state.indoorFeatures,
        }),
      };

    case 'outdoorFeatures':
      return {
        ...state,
        outdoorFeatures: getSelectedOptionsOrDefault({
          defaultValue: SEARCH_PROPERTY_FEATURES_DEFAULT_ITEM,
          currentSelection: value,
          previousSelection: state.outdoorFeatures,
        }),
      };

    case 'ecoFriendlyFeatures':
      return {
        ...state,
        ecoFriendlyFeatures: getSelectedOptionsOrDefault({
          defaultValue: SEARCH_PROPERTY_FEATURES_DEFAULT_ITEM,
          currentSelection: value,
          previousSelection: state.ecoFriendlyFeatures,
        }),
      };

    case 'minCarSpaces':
      return { ...state, minCarSpaces: value };

    case 'keywords':
      return { ...state, keywords: value };

    case 'excludeUnderOffer':
      return { ...state, excludeUnderOffer: value };

    case 'excludeDepositTaken':
      return { ...state, excludeDepositTaken: value };

    case 'hasImages':
      return { ...state, hasImages: value };

    default:
      return state;
  }
};
